import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag, Row, Col,
  Drawer, Form, message, InputNumber, Input, DatePicker, Statistic,
} from 'antd'
import { GoldOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const STATUS_COLORS: Record<string, string> = {
  IN_STORE: 'success',
  ALLOCATED: 'processing',
  DISPATCHED: 'default',
  REJECTED: 'error',
}

const GRADE_COLORS: Record<string, string> = {
  A: 'green',
  B: 'gold',
  C: 'orange',
}

export default function FGStorePage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [countFilter, setCountFilter] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingLot, setEditingLot] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [form] = Form.useForm()

  const { data: fgLots, isLoading } = useQuery({
    queryKey: ['fg-lots', plantId, statusFilter, countFilter],
    queryFn: () =>
      apiClient.get(`${API}/fg-lots`, {
        params: {
          plant_id: plantId,
          status: statusFilter || undefined,
          count_ne: countFilter || undefined,
          limit: 200,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: summary } = useQuery({
    queryKey: ['fg-summary', plantId],
    queryFn: () =>
      apiClient.get(`${API}/fg-summary`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: receivedIdts } = useQuery({
    queryKey: ['idt-received-fg', plantId],
    queryFn: () =>
      apiClient.get(`${API}/idt`, {
        params: { plant_id: plantId, status: 'RECEIVED', to_stage: 'FG_STORE', limit: 200 },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`${API}/fg-lots`, { ...data, plant_id: plantId }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fg-lots'] })
      qc.invalidateQueries({ queryKey: ['fg-summary'] })
      message.success('FG lot created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`${API}/fg-lots/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fg-lots'] })
      qc.invalidateQueries({ queryKey: ['fg-summary'] })
      message.success('FG lot updated')
      setDrawerOpen(false)
      setEditingLot(null)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const openCreate = () => {
    setEditingLot(null)
    form.resetFields()
    form.setFieldsValue({ production_date: dayjs() })
    setDrawerOpen(true)
  }

  const openEdit = (lot: any) => {
    setEditingLot(lot)
    form.setFieldsValue({
      ...lot,
      production_date: lot.production_date ? dayjs(lot.production_date) : dayjs(),
    })
    setDrawerOpen(true)
  }

  const handleSave = () => {
    form.validateFields().then(vals => {
      const payload = {
        ...vals,
        production_date: vals.production_date?.format('YYYY-MM-DD'),
      }
      if (editingLot) {
        updateMut.mutate({ id: editingLot.id, data: payload })
      } else {
        createMut.mutate(payload)
      }
    })
  }

  const lotList = Array.isArray(fgLots) ? fgLots : []
  const idtList = Array.isArray(receivedIdts) ? receivedIdts : []
  const sumData = summary ?? {}

  const columns = [
    {
      title: 'FG Lot No', dataIndex: 'fg_lot_no', key: 'fg_lot_no', width: 140,
      render: (v: string) => <b style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</b>,
    },
    {
      title: 'Production Date', dataIndex: 'production_date', key: 'production_date', width: 130,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—',
    },
    {
      title: 'Shift', dataIndex: 'shift', key: 'shift', width: 70,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Count', key: 'count', width: 130,
      render: (_: unknown, r: any) => `${r.count_ne ?? ''}s ${r.count_description ?? ''}`.trim() || '—',
    },
    {
      title: 'Grade', dataIndex: 'grade', key: 'grade', width: 80,
      render: (v: string) => <Tag color={GRADE_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Net Wt (kg)', dataIndex: 'net_weight_kg', key: 'net_weight_kg', width: 110, align: 'right' as const,
      render: (v: number) => v != null ? Number(v).toFixed(3) : '—',
    },
    {
      title: 'Allocated (kg)', dataIndex: 'allocated_kg', key: 'allocated_kg', width: 120, align: 'right' as const,
      render: (v: number) => v != null ? Number(v).toFixed(3) : '—',
    },
    {
      title: 'Available (kg)', key: 'available_kg', width: 120, align: 'right' as const,
      render: (_: unknown, r: any) => {
        const avail = Number(r.net_weight_kg ?? 0) - Number(r.allocated_kg ?? 0)
        return <span style={{ color: avail > 0 ? '#52c41a' : '#888', fontWeight: 600 }}>{avail.toFixed(3)}</span>
      },
    },
    {
      title: 'Cones', dataIndex: 'cone_count', key: 'cone_count', width: 80, align: 'right' as const,
    },
    {
      title: 'Cartons', dataIndex: 'carton_count', key: 'carton_count', width: 80, align: 'right' as const,
    },
    { title: 'Location', dataIndex: 'location', key: 'location', width: 100 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 70,
      render: (_: unknown, r: any) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <GoldOutlined style={{ marginRight: 8, color: '#faad14' }} />
        FG Store
      </Title>
      <Typography.Text type="secondary">Finished goods inventory — lots, weights, allocation</Typography.Text>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="Total In-Store (kg)" value={Number(sumData.total_kg ?? 0).toFixed(0)} suffix="kg" />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="Allocated (kg)" value={Number(sumData.allocated_kg ?? 0).toFixed(0)} suffix="kg" valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="Available (kg)" value={Number(sumData.available_kg ?? 0).toFixed(0)} suffix="kg" valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="Total Lots" value={lotList.length} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 140 }}
              value={statusFilter}
              onChange={v => setStatusFilter(v)}
              options={['IN_STORE', 'ALLOCATED', 'DISPATCHED', 'REJECTED'].map(s => ({ value: s, label: s.replace('_', ' ') }))}
            />
          </Col>
          <Col>
            <InputNumber
              placeholder="Count (Ne)"
              value={countFilter}
              onChange={v => setCountFilter(v)}
              min={1}
              style={{ width: 120 }}
            />
          </Col>
          <Col flex="auto" />
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New FG Lot
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={lotList}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 1300 }}
        />
      </Card>

      <Drawer
        title={editingLot ? `Edit: ${editingLot.fg_lot_no}` : 'New FG Lot'}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingLot(null); form.resetFields() }}
        width={540}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); setEditingLot(null); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending || updateMut.isPending}
                onClick={handleSave}
              >
                Save
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="fg_lot_no" label="FG Lot No">
            <Input placeholder="Auto-generated if blank" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="count_ne" label="Count (Ne)">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="count_description" label="Count Description">
                <Input placeholder="e.g. 30s Combed" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="production_date" label="Production Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="shift" label="Shift">
                <Select options={[
                  { value: 'A', label: 'Shift A' },
                  { value: 'B', label: 'Shift B' },
                  { value: 'C', label: 'Shift C' },
                ]} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="source_lot_no" label="Source Lot No">
                <Input placeholder="Cotton lot used" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="idt_id" label="IDT Reference (optional)">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={idtList.map((i: any) => ({
                    value: i.id,
                    label: `${i.idt_no} — ${i.lot_no ?? ''}`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="gross_weight_kg" label="Gross Weight (kg)">
                <InputNumber style={{ width: '100%' }} min={0} precision={3} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="net_weight_kg" label="Net Weight (kg)">
                <InputNumber style={{ width: '100%' }} min={0} precision={3} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="cone_count" label="Cone Count">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="carton_count" label="Carton Count">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="grade" label="Grade">
                <Select options={[
                  { value: 'A', label: 'A' },
                  { value: 'B', label: 'B' },
                  { value: 'C', label: 'C' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="location" label="Location">
                <Input placeholder="e.g. Rack B3" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
